import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { RoomEntity } from "@lifeat/shared/data-models/Rooms/RoomEntity";
import { MESSAGES_TO_FETCH_PAGE_SIZE } from "@lifeat/shared/rooms/room-chat/RoomChatConstants";
import { ChatMessage } from "@lifeat/shared/rooms/room-chat/RoomChatTypes";
import { Timeout } from "@lifeat/shared/util/TypeUtil";
import LinkButton from "@common/LifeAtButton/Variants/LinkButton";
import LoadingSpinner from "@common/LoadingSpinner/LoadingSpinner";
import ChatPlaceholderImage from "@resources/images/chat-placeholder-image.png";
import { FontSize } from "@themes/Fonts";
import { BrowserUtil, InteractUtil } from "@util/index";
import { useRoomContext } from "../../RoomContext";
import { getUsersPublicInfoArray } from "../../RoomService";
import { RoomParams } from "../../RoomTypes";
import { ChatContextType, useChatContext } from "../ChatContext";
import { ChatMessagesContextType, useChatMessagesContext } from "../ChatMessagesContext";
import { checkIfMessagesExistToBeLoaded } from "../RoomChatService";
import { ChatMessageDisplayState, RoomPublicUserInfoList } from "../RoomChatTypes";
import useRoomChatMessages from "../useRoomChatMessages";
import ChatMessageBubble from "./ChatMessageBubble";
import PinnedMessage from "./PinnedMessage";

const ChatContentContainer = styled.div`
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column-reverse;

    overflow-y: scroll;
`;

const ChatContent = styled.div`
    width: 100%;
    height: auto;

    display: flex;
    flex-direction: column;
    align-items: center;
`;

const PlaceHolderContainer = styled.div`
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
    padding: 20px 2px;
    text-align: center;
    line-height: 1.7;

    white-space: pre-line;
`;

const PlaceHolderHeaderText = styled.span`
    font-size: ${FontSize.normal};
    color: ${(props) => props.theme.textColorPrimary};
`;

const PlaceHolderSubText = styled.span`
    font-size: ${FontSize.small};
    color: ${(props) => props.theme.textColorSecondary};
`;

const PlaceHolderLink = styled.span`
    text-decoration: underline;
    cursor: pointer;
`;

const LinkCopiedText = styled.div`
    font-size: ${FontSize.small};
    color: ${(props) => props.theme.textColorSpecial};
    padding-top: 10px;
`;

const FetchMoreMessagesBtn = styled(LinkButton)`
    color: ${(props) => props.theme.textColorSecondary};
    font-size: ${FontSize.small};
`;

const LINK_COPIED_MESSAGE_TIMEOUT_MS = 5000;

const CHAT_PLACEHOLDER = `Invite your friends and start chatting! 👋`;
const CHAT_PLACEHOLDER_SHARE_ROOM = `\nShare your room link `;
const LINK_COPIED_MESSAGE = `✅ Link copied to clipboard!`;
const CHAT_PLACEHOLDER_COMMUNITY = `Read more about our community conduct `;
const LOAD_MORE_MESSAGES_BTN_LABEL = "Load more messages";

const COMMUNITY_GUIDELINES_URL = "https://lifeat.io/en-us/article/community-rules-and-guidelines-1msu1mz/";

export interface RoomChatMessagesContentProps {
    readonly roomInfo: RoomEntity | undefined;
    readonly isInteractingBlocked: boolean;
}
export default function RoomChatMessagesContent(props: RoomChatMessagesContentProps) {
    const { pinnedMessage }: ChatContextType = useChatContext();
    const { latestMessages }: ChatMessagesContextType = useChatMessagesContext();
    const { roomId }: RoomParams = useParams();
    const { roomInfo, isInteractingBlocked } = props;

    const { messageAuthors, setMessageAuthors } = useRoomContext();

    const [messagesPageSize, increasePageLimit] = useRoomChatMessages(roomId);

    const [showLinkCopiedMessage, setShowLinkCopiedMessage] = useState(false);
    const [shareableRoomLink, setShareableRoomLink] = useState<string>(window.location.href);
    useEffect(() => {
        setShareableRoomLink(window.location.href);
    }, [window.location.href]);

    useEffect(() => {
        let timeout: Timeout;
        if (showLinkCopiedMessage) {
            timeout = setTimeout(() => setShowLinkCopiedMessage(false), LINK_COPIED_MESSAGE_TIMEOUT_MS);
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [showLinkCopiedMessage]);

    const firstMsgRef = useRef(null);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
    useEffect(() => {
        if (
            Object.keys(latestMessages).length < MESSAGES_TO_FETCH_PAGE_SIZE * messagesPageSize &&
            !isLoadingMoreMessages
        ) {
            setHasMoreMessages(false);
        } else {
            const oldestMessageIdShown = Object.keys(latestMessages).sort()[0];
            if (oldestMessageIdShown && !firstMsgRef.current) {
                setIsLoadingMoreMessages(true);
                checkIfMessagesExistToBeLoaded(roomId, oldestMessageIdShown).then((hasMore: boolean) => {
                    // Mimic a false timeout/loading state
                    setTimeout(() => {
                        setHasMoreMessages(hasMore);
                        setIsLoadingMoreMessages(false);
                    }, 500);
                });
            } else {
                const hasMore =
                    firstMsgRef.current && oldestMessageIdShown ? firstMsgRef.current < oldestMessageIdShown : false;
                setHasMoreMessages(hasMore);
            }
        }
    }, [latestMessages, messagesPageSize, roomId]);

    const handleFetchMore = () => {
        if (hasMoreMessages) {
            increasePageLimit();
        }
    };

    useEffect(() => {
        if (roomInfo) {
            const latestMessagesAuthors = Object.values(latestMessages).map(
                (message: ChatMessage) => message?.authorUid,
            );
            const pinnedMessagesAuthors = pinnedMessage?.authorUid ? [pinnedMessage.authorUid] : [];
            const allMessagesAuthors = [...new Set([...latestMessagesAuthors, ...pinnedMessagesAuthors])];

            const existingAuthors = Object.keys(messageAuthors);
            const remainingAuthors = allMessagesAuthors.filter((uid: string) => !existingAuthors.includes(uid));

            if (remainingAuthors.length > 0) {
                getUsersPublicInfoArray(remainingAuthors, roomInfo.ownerUid, roomInfo.guests).then(
                    (newAuthors: RoomPublicUserInfoList) => {
                        setMessageAuthors({ ...messageAuthors, ...newAuthors });
                    },
                );
            }
        }
    }, [latestMessages, pinnedMessage, roomInfo]);

    const handleCopyLinkButtonClick = () => {
        InteractUtil.copyToClipboard(shareableRoomLink);
        setShowLinkCopiedMessage(true);
    };

    const generateChatMessageBubble = (id: string, message: ChatMessageDisplayState | undefined) => {
        if (id && message) {
            return (
                <ChatMessageBubble
                    key={`chat-message-${id}`}
                    messageId={id}
                    message={message.message}
                    authorInfo={messageAuthors[message.authorUid]}
                    timeStamp={message.sentTimestamp}
                    isInteractingBlocked={isInteractingBlocked}
                    sending={message.state === "sending"}
                />
            );
        }
        return null;
    };

    const messages = Object.keys(latestMessages)
        .sort()
        .map((id: string) => {
            return generateChatMessageBubble(id, latestMessages[id]);
        });

    const pinnedMessageBubble = pinnedMessage && (
        <PinnedMessage
            messageId={pinnedMessage.id}
            message={pinnedMessage.message}
            authorInfo={messageAuthors[pinnedMessage.authorUid]}
            timeStamp={pinnedMessage.sentTimestamp}
            isInteractingBlocked={isInteractingBlocked}
        />
    );

    const placeHolder = (
        <PlaceHolderContainer>
            <PlaceHolderHeaderText>
                {CHAT_PLACEHOLDER}
                {CHAT_PLACEHOLDER_SHARE_ROOM}
                <PlaceHolderLink onClick={handleCopyLinkButtonClick}>here</PlaceHolderLink>
                {showLinkCopiedMessage && <LinkCopiedText>{LINK_COPIED_MESSAGE}</LinkCopiedText>}
            </PlaceHolderHeaderText>
            <img src={ChatPlaceholderImage} />
            <PlaceHolderSubText>
                {CHAT_PLACEHOLDER_COMMUNITY}
                <PlaceHolderLink onClick={() => BrowserUtil.openLinkNewTab(COMMUNITY_GUIDELINES_URL)}>
                    here
                </PlaceHolderLink>
            </PlaceHolderSubText>
        </PlaceHolderContainer>
    );

    return (
        <>
            {pinnedMessageBubble}

            <ChatContentContainer>
                <ChatContent>
                    {hasMoreMessages && (
                        <FetchMoreMessagesBtn label={LOAD_MORE_MESSAGES_BTN_LABEL} handleClick={handleFetchMore} />
                    )}

                    {isLoadingMoreMessages && <LoadingSpinner />}

                    {Object.keys(latestMessages).length > 0 ? messages : placeHolder}
                </ChatContent>
            </ChatContentContainer>
        </>
    );
}
